import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { PageHero } from "../components/shared/Primitives";
import { api } from "../lib/api";
import { useHandoverSession } from "../hooks/useHandoverSession";

// Same-origin path validator. Only accepts a single-slash absolute path
// (e.g. "/family/care-needs"). Rejects "//attacker.com", "https://…",
// empty strings and anything unusual — protects against open-redirect
// abuse. Mirrors MagicLinkRequest.jsx's identical validator.
function safeNext(raw) {
  if (typeof raw !== "string") return null;
  const v = raw.trim();
  if (!v.startsWith("/") || v.startsWith("//")) return null;
  return v;
}

// Native replacement for the external Systeme "free-carer-access" /
// "families-free-sign-up" funnel pages. Two free JCC tiers only — paid
// tiers (Pro Carer Circle, Family Circle) are a separate, later stage.
const TIER_CONFIG = {
  "carer-starter": {
    entitlement: "carer_free",
    eyebrow: "Carer Starter",
    accent: "purple",
    bgTint: "carer",
    title: "Join Carer Starter — free",
    description:
      "Your free starting point as an independent carer: foundational guidance, sample templates and introductory articles. No payment required.",
    btnClass: "btn-purple",
    workspaceHref: "/workspace/carer-starter",
    requiresCarerDeclaration: true,
  },
  "family-starter": {
    entitlement: "family_free",
    eyebrow: "Family Starter",
    accent: "teal",
    bgTint: "family",
    title: "Join Family Starter — free",
    description:
      "Your free starting point for arranging independent care: introductory guides, sample interview questions and a plain-English overview. No payment required.",
    btnClass: "btn-teal",
    workspaceHref: "/workspace/family-starter",
  },
};

export default function JoinFree() {
  const { tier } = useParams();
  const config = TIER_CONFIG[tier];
  const session = useHandoverSession();

  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  // If the URL contains ?next=<safe-path>, persist it the same way
  // MagicLinkRequest.jsx does — free_join.py sends the SAME kind of
  // one-time magic-link email on joining, so MagicLinkChecking will
  // honour it after the new member clicks that link and redeems it,
  // returning them to whatever page sent them here to join (e.g. the
  // Care Needs form) instead of the default hub.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const n = safeNext(p.get("next"));
      if (n) {
        window.localStorage.setItem("jcc_handover_next", n);
      }
    } catch {
      /* localStorage unavailable — user falls back to the hub buttons */
    }
  }, []);

  if (!config) {
    return (
      <div className="container-jcc py-24 text-center" data-testid="join-free-unknown-tier">
        <h1 className="font-display text-2xl font-bold">Membership not found</h1>
        <p className="mt-2 text-ink-muted">
          Please choose <Link to="/for-carers" className="underline">For Carers</Link> or{" "}
          <Link to="/for-families" className="underline">For Families</Link> to find the right starting point.
        </p>
      </div>
    );
  }

  // A signed-in member who already holds this entitlement (directly, or
  // via inheritance from a paid tier — session.entitlements is already the
  // backend's fully-expanded list, e.g. family_circle -> +family_free)
  // should never be shown the sign-up form again. Covers every entry
  // point into this route (nav link, marketing CTAs, bookmarked URLs).
  const alreadyHasAccess = session.ready && session.active && session.entitlements.includes(config.entitlement);
  if (alreadyHasAccess) {
    return (
      <div data-testid={`join-free-already-member-${tier}`}>
        <PageHero eyebrow={config.eyebrow} title={config.title} description={config.description} accent={config.accent} bgTint={config.bgTint} />
        <section className="section">
          <div className="container-jcc max-w-lg mx-auto">
            <div className="card-base p-8 text-center" data-testid="join-free-already-member-card">
              <CheckCircle2 className={`w-12 h-12 mx-auto ${config.accent === "purple" ? "text-purple" : "text-teal"}`} />
              <h2 className="mt-4 font-display text-xl font-semibold">You already have {config.eyebrow} access</h2>
              <p className="mt-2 text-sm text-ink-muted">
                You're signed in and your membership already includes this. No need to join again — head straight to your workspace.
              </p>
              <Link to={config.workspaceHref} className={`${config.btnClass} mt-6 inline-flex items-center gap-2`} data-testid="join-free-go-to-workspace">
                Go to {config.eyebrow} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy || !email) return;
    if (config.requiresCarerDeclaration && (!firstName.trim() || !lastName.trim() || !declarationAccepted)) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/membership/free/join", {
        tier: config.entitlement,
        email: email.trim(),
        name: config.requiresCarerDeclaration ? undefined : (name.trim() || undefined),
        first_name: config.requiresCarerDeclaration ? firstName.trim() : undefined,
        last_name: config.requiresCarerDeclaration ? lastName.trim() : undefined,
        declaration_accepted: config.requiresCarerDeclaration ? declarationAccepted : undefined,
      });
      setResult(data);
    } catch (e2) {
      setError(e2.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid={`join-free-page-${tier}`}>
      <PageHero eyebrow={config.eyebrow} title={config.title} description={config.description} accent={config.accent} bgTint={config.bgTint} />

      <section className="section">
        <div className="container-jcc max-w-lg mx-auto">
          {!result ? (
            <form onSubmit={handleSubmit} className="card-base p-8 space-y-5" data-testid="join-free-form" noValidate>
              {config.requiresCarerDeclaration ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="join-first-name" className="block text-sm font-medium mb-1.5">First name</label>
                    <input
                      id="join-first-name"
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      data-testid="join-free-first-name-input"
                      className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </div>
                  <div>
                    <label htmlFor="join-last-name" className="block text-sm font-medium mb-1.5">Last name</label>
                    <input
                      id="join-last-name"
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      data-testid="join-free-last-name-input"
                      className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label htmlFor="join-name" className="block text-sm font-medium mb-1.5">Name (optional)</label>
                  <input
                    id="join-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="join-free-name-input"
                    className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
              )}
              <div>
                <label htmlFor="join-email" className="block text-sm font-medium mb-1.5">Email address</label>
                <input
                  id="join-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="join-free-email-input"
                  className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>
              {config.requiresCarerDeclaration && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <label className="flex items-start gap-3 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={declarationAccepted}
                      onChange={(e) => setDeclarationAccepted(e.target.checked)}
                      data-testid="join-free-declaration-checkbox"
                      className="mt-0.5 w-4 h-4"
                    />
                    <span>
                      I confirm that I am registering as an individual carer in my own name. I am not
                      registering on behalf of a care agency, care provider, recruitment business or
                      other organisation.
                    </span>
                  </label>
                  <p className="mt-2 text-xs text-ink-muted">
                    Carer accounts are for individual carers only. Business and organisational accounts will be removed.
                  </p>
                </div>
              )}
              {error && <p className="text-sm text-red-600" data-testid="join-free-error">{error}</p>}
              <button
                type="submit"
                disabled={busy || !email || (config.requiresCarerDeclaration && (!firstName.trim() || !lastName.trim() || !declarationAccepted))}
                data-testid="join-free-submit-button"
                className={`${config.btnClass} w-full disabled:opacity-50`}
              >
                {busy ? "Joining…" : `Join ${config.eyebrow} — free`}
              </button>
              <p className="text-xs text-ink-muted text-center">
                No card required. We'll email you a secure one-time sign-in link.
              </p>
            </form>
          ) : (
            <div className="card-base p-8 text-center" data-testid="join-free-confirmation">
              <CheckCircle2 className={`w-12 h-12 mx-auto ${config.accent === "purple" ? "text-purple" : "text-teal"}`} />
              <h2 className="mt-4 font-display text-xl font-semibold">{result.message}</h2>
              <p className="mt-2 text-sm text-ink-muted">
                Once you sign in, you'll be taken straight to where you left off.
              </p>
              <Link to="/" className="btn-outline mt-6 inline-flex items-center gap-2">
                Back to homepage <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
